/**
 * Paper viewport layer utility.
 *
 * Manages dedicated SVG <g> groups inside the JointJS paper's transform-applied
 * layer group. Children appended to a layer obtained here automatically inherit
 * the paper's zoom/pan transform, so callers do not need any coordinate-space
 * conversion.
 *
 * This is intended as the shared substrate for ad-hoc visual overlays
 * (relationship lines, smart guides, future selection halos, etc.) that must
 * NOT be persisted into graph.toJSON().
 *
 * Compatibility note:
 *   - JointJS v3 marks the transformed group with class `joint-viewport`.
 *   - JointJS v4 (used by this project) replaced that with `joint-layers`,
 *     which contains `joint-cells-layer`, `joint-tools-layer`, etc.
 *   The lookup walks the v4 names first, then the v3 name, then any other
 *   plausible candidate so the utility keeps working across versions.
 */

import { dia } from '@joint/core'

const SVG_NS = 'http://www.w3.org/2000/svg'

/**
 * Locate the SVG `<g>` element that has the paper's zoom/pan transform applied.
 * We try the JointJS v4 markup first, then fall back to v3 / heuristic targets.
 */
function findTransformedRoot(svg: SVGSVGElement): SVGGElement | null {
  // JointJS v4: `.joint-layers` is the top-level transformed group.
  const v4 = svg.querySelector('.joint-layers') as SVGGElement | null
  if (v4) return v4
  // JointJS v3 (and some intermediates): `.joint-viewport`.
  const v3 = svg.querySelector('.joint-viewport') as SVGGElement | null
  if (v3) return v3
  // Last resort: the first <g> child of the paper SVG. JointJS always renders
  // a single transformed group as the first non-defs child.
  const firstG = svg.querySelector(':scope > g') as SVGGElement | null
  return firstG
}

/**
 * Returns an existing layer matching `layerId` inside the paper's transform
 * group, or creates one. Returns null if the paper SVG / transform group are
 * not yet ready (e.g. before paper has finished its initial layout).
 *
 * The layer has `pointer-events: none` so it never intercepts canvas clicks.
 * Idempotent: calling repeatedly with the same id yields the same group.
 */
export function getOrCreateViewportLayer(
  paper: dia.Paper,
  layerId: string,
): SVGGElement | null {
  const svg = (paper as { svg?: SVGSVGElement }).svg
  if (!svg) return null
  const root = findTransformedRoot(svg)
  if (!root) return null

  const existing = root.querySelector(`#${CSS.escape(layerId)}`) as SVGGElement | null
  if (existing) return existing

  const group = document.createElementNS(SVG_NS, 'g')
  group.id = layerId
  group.style.pointerEvents = 'none'
  root.appendChild(group)
  return group
}

/** Removes all children from the layer (cleanup before re-render). */
export function clearLayer(group: SVGGElement): void {
  while (group.firstChild) group.removeChild(group.firstChild)
}

/** Detaches the layer from the DOM (use on hook unmount). */
export function removeLayer(group: SVGGElement): void {
  group.remove()
}
